// Shared chunk-size indicator used by both the legacy map editor
// (SourceDocumentMapPage) and the new two-pane review (MapReviewPage /
// MapTree / SourceDocPane). Extracted here so the badge palette + tier
// → tooltip mapping live in one place.
//
// Tier colours come from packages/shared/src/source-document-mapping-api.
// ts (classifyChunkSize thresholds) — grey/green/amber/red for
// tiny/good/large/oversized. Tooltip copy depends on WHERE the badge is
// used (chapter vs chunk), so callers pass an `isChunk` flag.
//
// MR 9 additions:
//   - `wasEstimatedTokens?` — when set + differs from current, renders
//     a `↓ was ~N` (or `↑`) delta chip. Persists until save (when the
//     baseline flips to the new value and the delta vanishes on its
//     own). The reviewer sees not just "this is amber" but "this was
//     red — you fixed it."
//   - `veryShortThreshold?` (default 200) — when the badge is on a
//     chunk and tokens fall below the threshold, appends `· very short`.
//     Counter-warning for over-split; the mock's spec calls this out
//     because splitting is now easy enough to over-do.
//   - Scale flash animation — a 0.9s pop (1 → 1.16 → 1) plays whenever
//     `estimatedTokens` changes after mount. Skipped on first render so
//     the whole page doesn't flash on load. Respects
//     `prefers-reduced-motion` via the CSS keyframes.

import { useEffect, useRef, useState } from 'react';
import type { ChunkSizeTier } from '@gc-coach/shared';
import { classifyChunkSize } from '@gc-coach/shared';
import styles from './ChunkSizeBadge.module.css';

export interface ChunkSizeBadgeProps {
  estimatedTokens: number;
  /** True when this element itself becomes a chunk (sub-section always;
   *  chapter only when it has zero sub-sections). Controls tooltip copy
   *  + whether the "very short" warning applies. */
  isChunk: boolean;
  /** Last-saved token count. When present + different from
   *  estimatedTokens, the badge renders a `↓/↑ was ~N` delta chip beside
   *  the count so the reviewer sees the improvement (or regression). */
  wasEstimatedTokens?: number;
  /** Tokens below this count on a chunk trigger the "very short"
   *  suffix. Default 200 — matches the strings spec §5. */
  veryShortThreshold?: number;
  t: (key: string) => string;
}

export function ChunkSizeBadge({
  estimatedTokens,
  isChunk,
  wasEstimatedTokens,
  veryShortThreshold = 200,
  t,
}: ChunkSizeBadgeProps) {
  const tier: ChunkSizeTier = classifyChunkSize(estimatedTokens) ?? 'good';
  const palette = TIER_PALETTE[tier];
  const tooltipKey = isChunk
    ? `mapping.chunkSizeTooltipChunk.${tier}`
    : `mapping.chunkSizeTooltipChapter.${tier}`;

  // Flash animation trigger. Bumped every time estimatedTokens changes
  // AFTER the initial mount — a fresh key on the wrapping <span> forces
  // React to remount it, which replays the CSS animation. First render
  // uses key=0 and doesn't fire (see prevTokens.current comparison).
  const [flashKey, setFlashKey] = useState(0);
  const prevTokens = useRef(estimatedTokens);
  useEffect(() => {
    if (prevTokens.current !== estimatedTokens) {
      prevTokens.current = estimatedTokens;
      setFlashKey((k) => k + 1);
    }
  }, [estimatedTokens]);

  const showDelta =
    wasEstimatedTokens !== undefined && wasEstimatedTokens !== estimatedTokens;
  const deltaArrow =
    showDelta && (wasEstimatedTokens as number) > estimatedTokens ? '↓' : '↑';
  const veryShort = isChunk && estimatedTokens < veryShortThreshold;

  return (
    <span className={styles.wrap}>
      <span
        key={flashKey}
        className={styles.badge}
        title={t(tooltipKey)}
        style={{
          background: palette.bg,
          color: palette.fg,
          borderColor: palette.border,
        }}
      >
        <span
          aria-hidden="true"
          className={styles.dot}
          style={{ background: palette.dot }}
        />
        ~{estimatedTokens.toLocaleString()} tok
        {veryShort && (
          <span className={styles.veryShort}>
            · {t('mapReview.chunkVeryShort')}
          </span>
        )}
      </span>
      {showDelta && (
        <span className={styles.delta} aria-live="polite">
          {deltaArrow} {t('mapReview.chunkWas')} ~
          {(wasEstimatedTokens as number).toLocaleString()}
        </span>
      )}
    </span>
  );
}

const TIER_PALETTE: Record<
  ChunkSizeTier,
  { bg: string; fg: string; border: string; dot: string }
> = {
  tiny: {
    bg: 'rgba(139, 141, 156, 0.10)',
    fg: '#565869',
    border: 'rgba(139, 141, 156, 0.35)',
    dot: '#8B8D9C',
  },
  good: {
    bg: 'rgba(10, 122, 101, 0.10)',
    fg: '#0A7A65',
    border: 'rgba(10, 122, 101, 0.35)',
    dot: '#0A7A65',
  },
  large: {
    bg: 'rgba(217, 119, 6, 0.10)',
    fg: '#B45309',
    border: 'rgba(217, 119, 6, 0.35)',
    dot: '#D97706',
  },
  oversized: {
    bg: 'rgba(176, 64, 64, 0.10)',
    fg: '#B04040',
    border: 'rgba(176, 64, 64, 0.35)',
    dot: '#B04040',
  },
};
